import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import {
  hasCoordinates,
  LEAFLET_MARKER_ICON_URL,
  type Coordinates,
} from '../utils/map';
import { calculateDistance } from '../utils/distance';
import tw from '../helpers/tw';
import imageFadeIn from '../modifiers/image-fade-in';
import posterUrl from '../helpers/poster-url';
import { type ItemResource, parseEnrichedData } from './item-card/utils.gts';
import { field, asString, recordAsMap } from '../utils/enriched-data';
import TypeBadge from './type-badge.gts';
import MovieDetails from './item-card/movie-details.gts';
import BookDetails from './item-card/book-details.gts';
import MusicDetails from './item-card/music-details.gts';
import PlaceDetails from './item-card/place-details.gts';
import PersonDetails from './item-card/person-details.gts';
import DefaultDetails from './item-card/default-details.gts';
import Icon from './icon';
import TagPicker from './tag-picker.gts';
import type { UserItem } from '../schemas/user-item.ts';
import type { Tag } from '../schemas/tag.ts';
import type { TOC } from '@ember/component/template-only';
import CheckIcon from '~icons/lucide/check';
import XIcon from '~icons/lucide/x';
import UndoIcon from '~icons/lucide/undo-2';

export type { ItemResource };

interface ItemCardSignature {
  Args: {
    item: ItemResource;
    /** Owner model — when present, action buttons are shown */
    userItem?: UserItem;
    onDone?: () => void;
    onDismiss?: () => void;
    onRestore?: () => void;
    disabled?: boolean;
    tags?: Tag[];
    onTagsChanged?: () => void;
    /** Optional user location for distance calculation (places) */
    userLocation?: Coordinates | null;
  };
  Element: HTMLDivElement;
}

/**
 * Dynamic component dispatch — returns the right details
 * component for each item type.
 */
function detailsFor(item: ItemResource) {
  const type = asString(field(item, 'type'));
  if (type === 'movie' || type === 'tv_show') return MovieDetails;
  if (type === 'book') return BookDetails;
  if (type === 'music') return MusicDetails;
  if (type === 'place') return PlaceDetails;
  if (type === 'person') return PersonDetails;
  return DefaultDetails;
}

/** Parse enrichedData for the template */
function enrichedData(item: ItemResource) {
  return parseEnrichedData(field(item, 'enrichedData'));
}

/** Safe string attribute access */
function attr(item: ItemResource, key: string): string {
  return asString(field(item, key));
}

/** Safe number attribute access */
function numAttr(item: ItemResource, key: string): number | undefined {
  const v = field(item, key);
  return typeof v === 'number' ? v : undefined;
}

function showPlaceMapThumbnail(item: ItemResource): boolean {
  const data = parseEnrichedData(field(item, 'enrichedData'));
  if (asString(field(item, 'type')) !== 'place') return false;
  if (!hasCoordinates(data?.location)) return false;
  // Only show static map tile with marker overlay when there is no real photo
  return !asString(data?.photoUrl);
}

function hasConsensus(item: ItemResource): boolean {
  const count = field(item, 'opinionCount');
  return typeof count === 'number' && count > 0;
}

function consensusClass(item: ItemResource): string {
  const pct = field(item, 'recommendationPct');
  if (typeof pct !== 'number') return 'badge-neutral';
  if (pct >= 60) return 'badge-success';
  if (pct >= 40) return 'badge-warning';
  return 'badge-error';
}

/** Get emoji for places */
function itemEmoji(item: ItemResource): string {
  if (asString(field(item, 'type')) !== 'place') return '';
  const emoji = field(item, 'emoji');
  return typeof emoji === 'string' ? emoji : '';
}

/** Calculate distance for place items */
function placeDistance(
  item: ItemResource,
  userLocation?: Coordinates | null
): string | null {
  if (asString(field(item, 'type')) !== 'place') return null;
  if (!userLocation) return null;

  const data = parseEnrichedData(field(item, 'enrichedData'));
  const itemLocation = data?.location;
  if (!hasCoordinates(itemLocation)) return null;

  return calculateDistance(userLocation, itemLocation);
}

const ItemCard: TOC<ItemCardSignature> = <template>
  <div class="relative" ...attributes>
    <LinkTo
      @route="items.show"
      @model={{@item.id}}
      class="card card-side bg-base-100 overflow-hidden no-underline shadow-md transition-shadow hover:shadow-lg"
    >
      {{#if (showPlaceMapThumbnail @item)}}
        <div
          class="bg-base-200 relative w-20 shrink-0 overflow-hidden p-0 sm:w-28"
        >
          <img
            {{imageFadeIn}}
            src={{posterUrl (recordAsMap @item)}}
            alt=""
            class="h-full w-full object-cover opacity-0 transition-opacity duration-300"
            loading="lazy"
          />
          <img
            src={{LEAFLET_MARKER_ICON_URL}}
            alt=""
            aria-hidden="true"
            class="pointer-events-none absolute top-1/2 left-1/2 h-10.25 w-6.25 -translate-x-1/2 -translate-y-full"
          />
        </div>
      {{else}}
        <div class="bg-base-200 w-20 shrink-0 overflow-hidden p-0 sm:w-28">
          <img
            {{imageFadeIn}}
            src={{posterUrl (recordAsMap @item)}}
            alt=""
            class="h-full w-full object-cover opacity-0 transition-opacity duration-300"
            loading="lazy"
          />
        </div>
      {{/if}}
      <div class="card-body min-w-0 p-3 sm:p-4">
        <h3 class="card-title truncate text-sm sm:text-base">
          {{#if (itemEmoji @item)}}
            <span aria-hidden="true">{{itemEmoji @item}}</span>
          {{/if}}
          {{attr @item "name"}}
        </h3>
        <div class="flex flex-wrap items-center gap-2">
          <TypeBadge @type={{attr @item "type"}} />
          {{#if (numAttr @item "matchedCount")}}
            <span class="text-base-content/60 text-xs">
              {{numAttr @item "matchedCount"}}
              mentions
            </span>
          {{/if}}
          {{#if (hasConsensus @item)}}
            <span
              class={{tw "badge badge-sm" (consensusClass @item)}}
              title="Recommendation consensus"
            >
              🍅
              {{numAttr @item "recommendationPct"}}%
            </span>
          {{/if}}
        </div>
        {{! Dynamic type-specific details }}
        {{#let (detailsFor @item) as |Details|}}
          <Details
            @data={{enrichedData @item}}
            @item={{@item}}
            @distance={{placeDistance @item @userLocation}}
          />
        {{/let}}
      </div>
    </LinkTo>

    {{! Owner action buttons — positioned outside LinkTo }}
    {{#if @userItem}}
      <div class="absolute right-2 bottom-2 flex gap-1">
        {{#if (eq @userItem.status "dismissed")}}
          {{#if @onRestore}}
            <button
              type="button"
              class="btn btn-xs btn-ghost"
              title="Restore"
              disabled={{@disabled}}
              {{on "click" @onRestore}}
            >
              <Icon @icon={{UndoIcon}} class="h-3 w-3" />
            </button>
          {{/if}}
        {{else}}
          {{#if (eq @userItem.status "active")}}
            {{#if @onDone}}
              <button
                type="button"
                class="btn btn-xs btn-success btn-outline"
                title="Mark as done"
                disabled={{@disabled}}
                {{on "click" @onDone}}
              >
                <Icon @icon={{CheckIcon}} class="h-3 w-3" />
                Done
              </button>
            {{/if}}
          {{/if}}

          {{#if (eq @userItem.status "watched")}}
            {{#if @onRestore}}
              <button
                type="button"
                class="btn btn-xs btn-ghost"
                title="Undo done"
                aria-label="Restore to active"
                disabled={{@disabled}}
                {{on "click" @onRestore}}
              >
                <Icon @icon={{UndoIcon}} class="h-3 w-3" />
              </button>
            {{/if}}
          {{/if}}

          {{#if @onDismiss}}
            <button
              type="button"
              class="btn btn-xs btn-ghost"
              title="Dismiss"
              disabled={{@disabled}}
              {{on "click" @onDismiss}}
            >
              <Icon @icon={{XIcon}} class="h-3 w-3" />
            </button>
          {{/if}}

          {{#if @tags}}
            <TagPicker
              @userItemId={{@userItem.id}}
              @tags={{@tags}}
              @onTagsChanged={{@onTagsChanged}}
            />
          {{/if}}
        {{/if}}
      </div>

      {{! Status badges }}
      {{#if (eq @userItem.status "watched")}}
        <div class="badge badge-success absolute top-2 right-2">
          ✓ Done
        </div>
      {{/if}}
      {{#if (eq @userItem.status "dismissed")}}
        <div class="badge badge-ghost absolute top-2 right-2">
          Dismissed
        </div>
      {{/if}}
    {{/if}}
  </div>
</template>;

export default ItemCard;
